// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import FormDialog from 'Ui/components/common/form-dialog/form-dialog';
import FormValidator from 'Ui/components/higher-order-components/form-validator';
import {closeDialog as closeReduxDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {postInventoryTransfers} from 'Services/inventory-service';
import {useSave} from 'Ui/react-hooks/use-save';
import SelectedPartQuantityFromStock from 'Ui/components/common/parts-selectors/selected-part-quantity-from-stock';
import PartSelector from 'Ui/components/common/parts-selectors/part-selector';
import InventoryPartsTotalCost from 'Ui/features/onequip/parts/inventory/inventory-parts-total-cost';
import {asNumber} from 'Utils/conversion-utils';

function PullInventoryDialog(props) {
    const {
        availableParts,
        closeDialog,
        currencyPreference,
        initialSelectedPart,
        invalidInputs,
        inventories,
        isgToggles,
        onClose,
        setValid,
        translations
    } = props;

    const [selectedParts, setSelectedParts] = React.useState(initialSelectedPart ? [{
        ...initialSelectedPart,
        id: initialSelectedPart.partId,
        quantity: 0,
        title: initialSelectedPart.partNumber,
        subtitle: `${initialSelectedPart.manufacturerName} - ${initialSelectedPart.name}`
    }] : []);

    const [saveFunc, disableSave, isSaving] = useSave(async () => {
        const partTransfers = selectedParts.map((part) => ({
            ...part,
            available: asNumber(part.stock),
            quantity: asNumber(part.quantity),
            totalCost: asNumber(part.totalCost),
            unitCost: asNumber(part.unitCost)
        }));

        const transferData = {
            isPull: true,
            sourceInventoryId: inventories[0].inventoryId,
            partTransfers
        };

        await postInventoryTransfers(transferData);

        if (onClose) {
            onClose();
        }

        closeDialog();
    }, {
        invalidInputs
    });

    const initializedAvailableParts = React.useMemo(() => availableParts.map((part) => ({
        ...part,
        quantity: 0
    })), [availableParts]);

    return (
        <FormDialog
            cancelLabel={translations.CANCEL}
            className='pull-inventory-dialog dropdown-overflow-dialog'
            closeHandler={closeDialog}
            disableSave={disableSave || selectedParts.length === 0}
            footerLoading={isSaving}
            onSave={saveFunc}
            saveLabel={translations.ONLINK_PULL_INVENTORY}
            title={translations.ONLINK_PULL_INVENTORY}
            translations={translations}
            width={'md'}
        >
            <form className='pull-inventory-form'>
                <PartSelector
                    additionalSubTitleSelector='partNumber'
                    availableParts={initializedAvailableParts}
                    currencyPreference={currencyPreference}
                    disabled={false}
                    inventories={inventories}
                    isgToggles={isgToggles}
                    partsInputComponent={SelectedPartQuantityFromStock}
                    selectedParts={selectedParts}
                    selectedPartsClassName={'selected-parts'}
                    setSelectedParts={setSelectedParts}
                    setValid={setValid}
                    showAddNewPartOption={false}
                    subTitleSelector='manufacturerName'
                    titleSelector='name'
                    translations={translations}
                />
                <InventoryPartsTotalCost
                    currencyPreference={currencyPreference}
                    isgToggles={isgToggles}
                    selectedParts={selectedParts}
                    translations={translations}
                />
            </form>
        </FormDialog>
    );
}

PullInventoryDialog.propTypes = {
    availableParts: PropTypes.arrayOf(PropTypes.object),
    closeDialog: PropTypes.func,
    currencyPreference: PropTypes.string,
    initialSelectedPart: PropTypes.object,
    invalidInputs: PropTypes.instanceOf(Set),
    inventories: PropTypes.arrayOf(PropTypes.object),
    isgToggles: PropTypes.object,
    onClose: PropTypes.func,
    setValid: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        translations: state.translations,
        currencyPreference: state.membership.currencyPreference,
        isgToggles: state.account.isgToggles
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeDialog() {
            dispatch(closeReduxDialog(dialogTypes.PULL_INVENTORY_DIALOG));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(FormValidator(PullInventoryDialog));
